import { AGREEMENTS_RECORD_TYPE_ALL_VALUE } from '../../agreements/agreements.constants';
import {
    REPORT_RECORD_COUNT_ELEMENT_ID,
    resolveReportRecordCount,
} from '../../agreements/report-record-count.constants';
import type { TaskPaneReportFilter } from '../../agreements/models/agreements.model';
import { getInputValue, getMultiSelectValues } from '../../shared/dom.util';
import { localCalendarDateInputToUtcIsoStartOfDay } from '../../shared/utils/local-calendar-date-to-utc-iso.util';
import { ReportingTargetFilterRegistry } from '../reporting-target/reporting-target-filter-registry';

const reportingTargetFilterRegistry = new ReportingTargetFilterRegistry();

/** Status codes from checkbox multiselect values (numeric strings `"0"`–`"5"`). */
function statusCodesFromMultiselectValues(selected: string[]): number[] {
    const codes = selected
        .map((v) => Number(v.trim()))
        .filter((n) => Number.isInteger(n) && n >= 0 && n <= 5);
    return Array.from(new Set(codes));
}

function applyRecordTypeFilterFromDom(filter: TaskPaneReportFilter): void {
    const target = getInputValue('report-target');
    const definition = reportingTargetFilterRegistry.getFilterByReportingTarget(target);
    const spec = definition.recordTypeFilter;
    if (!spec || spec.payloadBinding !== 'agreementType') {
        return;
    }

    const rawInitial = getInputValue('filter-record-type');
    const raw = rawInitial === '' ? spec.defaultValue : rawInitial;
    if (raw === AGREEMENTS_RECORD_TYPE_ALL_VALUE) {
        return;
    }

    const n = Number.parseInt(raw, 10);
    filter.agreementType = n === 0 || n === 1 ? n : Number(spec.defaultValue);
}

/**
 * Reads Agreements-shaped filter fields from the task pane DOM (target-dependent record type, shared fields).
 */
export function readAgreementsTaskPaneFilterFromDom(): TaskPaneReportFilter {
    const filter: TaskPaneReportFilter = {};
    const recordCountRaw = getInputValue(REPORT_RECORD_COUNT_ELEMENT_ID);
    const parsedRecordCount = Number.parseInt(recordCountRaw, 10);
    filter.recordCount = resolveReportRecordCount(
        Number.isNaN(parsedRecordCount) ? undefined : parsedRecordCount
    );

    const facilityCodes = getMultiSelectValues('filter-facility-codes');
    const statuses = statusCodesFromMultiselectValues(getMultiSelectValues('filter-statuses'));
    const expireDateFrom = getInputValue('filter-expire-date-from');

    applyRecordTypeFilterFromDom(filter);

    if (facilityCodes.length > 0) {
        filter.facilityCodes = facilityCodes;
    }

    if (statuses.length > 0) {
        filter.statuses = statuses;
    }

    if (expireDateFrom) {
        const iso = localCalendarDateInputToUtcIsoStartOfDay(expireDateFrom);
        if (iso) {
            filter.expireDateFrom = iso;
        }
    }

    return filter;
}
